// frontend/src/components/homepage/BlogLive.js
//
// "מהבלוג" - the three newest articles as cards, a literal port of the
// prototype's `.blog` block (P.10 of the homepage port).
//
// THREE CARDS, NOT A COVERFLOW. The prototype shows the three newest articles
// side by side and stacks them on a phone (`.posts` goes to one column under
// 900px); the current homepage's rotating coverflow is not in it and is not
// ported. Every card is a real RouterLink, so the prerendered snapshot carries
// the three links and their image alts for crawlers.
//
// ORDER IS CURATED, NOT CHRONOLOGICAL (owner round 4: "pin the 3 most
// valuable/interesting/attracting articles"). The strip used to be
// `sortArticleIndex(SORT_NEWEST).slice(0, 3)`, which made the homepage's only
// editorial slot a function of whatever shipped last. `PINNED_SLUGS` below is
// the list, and it is the ONE place to change it.
//
// The newest-first comparator is still here as the FALLBACK: a pinned slug that
// no longer exists (renamed, retired) silently dropping a card would leave the
// section short, so `pickArticles` fills any gap from the newest articles that
// are not already pinned, keeping the pinned ones in their listed order. That
// path is test-locked - it is the branch nobody would notice was broken.
//
// blogINDEX, not blogData: this component is on the landing page's eager path
// and blogData carries every article's prose (~85KB). Do not "simplify" this
// back to blogData.
//
// The card's category chip is the article's FIRST tag, as on the current
// homepage strip. The prototype's chips were typed by hand and do not exist in
// blogIndex.js.

import React, { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import SectionShell from "./SectionShell";
import Arrow from "./Arrow";
import useReveal from "../../motion/useReveal";
import {
  articleIndex,
  sortArticleList,
  BLOG_BASE,
  SORT_NEWEST,
} from "../../content/blog/blogIndex";
import BlogImage from "../blog/BlogImage";
import "../../homepage/pricingFaqBlog.css";

/** How many articles the strip shows - the prototype's three. */
export const CARD_COUNT = 3;

/** The section copy - one constant per language. */
export const HEADING = {
  he: {
    eyebrow: "מהבלוג",
    title: "מדריכים וטיפים פרקטיים על לידים, שיווק וסוכני AI.",
    readMore: "קראו עוד",
    all: "לכל המאמרים",
  },
  en: {
    eyebrow: "From the Blog",
    title: "Practical guides on leads, marketing and AI agents.",
    readMore: "Read more",
    all: "All articles",
  },
};

/**
 * The three articles the homepage pins, in display order.
 *
 * Chosen for pull toward a signup, not for recency - this strip sits between
 * the FAQ and the footer, so its readers are already most of the way down the
 * page and one click from the CTA:
 *
 *  1. leads-from-facebook-groups - the pillar. It is the product's promise
 *     written out, and "לידים" is the phrase this market actually searches.
 *  2. paid-ads-reality-check - the highest-intent pain we address: a business
 *     already spending on ads and unable to tell whether the numbers are
 *     normal. It ends where our alternative begins.
 *  3. hot-vs-cold-leads - makes the product's core differentiator (timing, and
 *     therefore the heat rating) legible before anyone sees the dashboard.
 *
 * Owner round 4; he may swap any of them - edit this list and nothing else.
 */
export const PINNED_SLUGS = [
  "leads-from-facebook-groups",
  "paid-ads-reality-check",
  "hot-vs-cold-leads",
];

/**
 * The pinned articles, padded to `CARD_COUNT` with the newest unpinned ones.
 *
 * Exported for the test rather than kept private: the fallback is the half of
 * this that never runs in production, so it has to be exercisable directly.
 *
 * @param {Array} index the article index to draw from
 * @param {string[]} pinned slugs to show first, in order
 * @param {number} count how many cards the strip has room for
 */
export function pickArticles(index, pinned, count) {
  const bySlug = new Map(index.map((article) => [article.slug, article]));
  const picked = pinned.map((slug) => bySlug.get(slug)).filter(Boolean);
  if (picked.length >= count) return picked.slice(0, count);

  const taken = new Set(picked.map((article) => article.slug));
  for (const article of sortArticleList(index, SORT_NEWEST)) {
    if (picked.length >= count) break;
    if (!taken.has(article.slug)) {
      picked.push(article);
      taken.add(article.slug);
    }
  }
  return picked;
}

const ITEMS = pickArticles(articleIndex, PINNED_SLUGS, CARD_COUNT);

/**
 * One article card. The whole card is the link; the reveal delay staggers the
 * three entrances exactly as the prototype's `--d`.
 */
function PostCard({ article, delay, readMore, ltr }) {
  const reveal = useReveal();
  const className = reveal.className ? `hl-blog-post ${reveal.className}` : "hl-blog-post";
  return (
    <RouterLink
      to={`${BLOG_BASE}/${article.slug}`}
      ref={reveal.ref}
      className={className}
      style={delay ? { "--hl-d": delay } : undefined}
    >
      <div className="hl-blog-img">
        {article.image && (
          <BlogImage
            src={article.image}
            alt={article.title}
            sizes="(max-width: 900px) 100vw, 33vw"
          />
        )}
      </div>
      <div className="hl-blog-bd">
        {article.tags?.[0] && <span className="hl-blog-tag">{article.tags[0]}</span>}
        <h3>{article.title}</h3>
        <p>{article.excerpt}</p>
        {/* "קראו עוד ←" - the arrow is its own span so the card's hover can
            nudge it (pricingFaqBlog.css `.hl-blog-rm-arr`). */}
        <span className="hl-blog-rm">
          {`${readMore} `}
          <Arrow
            className={ltr ? "hl-blog-rm-arr hl-blog-rm-arr-ltr" : "hl-blog-rm-arr"}
            direction={ltr ? "right" : "left"}
          />
        </span>
      </div>
    </RouterLink>
  );
}

/**
 * @param {object} props
 * @param {boolean} [props.isHebrew]
 * @param {string[]} [props.slugs] which articles to show, in order - pinned
 *   the same way `PINNED_SLUGS` is, so an unknown slug is skipped and the
 *   strip is padded to `CARD_COUNT` with the newest unpinned articles rather
 *   than rendering short. Left out, the module's own selection is used
 *   UNCHANGED (the same array object), so the homepage renders exactly what it
 *   did before this prop existed.
 */
export default function BlogLive({ isHebrew = true, slugs }) {
  const t = isHebrew ? HEADING.he : HEADING.en;
  const head = useReveal();
  const all = useReveal();
  const items = useMemo(
    () => (slugs ? pickArticles(articleIndex, slugs, CARD_COUNT) : ITEMS),
    [slugs]
  );

  if (items.length === 0) return null;

  return (
    <SectionShell
      id="blog"
      scene="blog"
      mesh="disperse"
      meshAlpha=".2"
      className="hl-blog"
      // The prototype's `.blog{padding-top:40px}`.
      sx={{ pt: { xs: "40px", md: "40px" } }}
    >
      <div ref={head.ref} className={head.className ? `hl-head ${head.className}` : "hl-head"}>
        <div className="hl-eyebrow">{t.eyebrow}</div>
        <h2 className="hl-h2">{t.title}</h2>
      </div>

      <div className="hl-blog-posts">
        {items.map((article, i) => (
          <PostCard
            key={article.slug}
            article={article}
            delay={i === 0 ? undefined : `.${i}s`}
            readMore={t.readMore}
            ltr={!isHebrew}
          />
        ))}
      </div>

      <div ref={all.ref} className={all.className ? `hl-blog-all ${all.className}` : "hl-blog-all"}>
        <RouterLink to={BLOG_BASE} className="hl-blog-btn">
          {`${t.all} `}
          <Arrow className="hl-blog-arr" direction={isHebrew ? "left" : "right"} />
        </RouterLink>
      </div>
    </SectionShell>
  );
}
